import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { NgSelectComponent } from '@ng-select/ng-select';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import {
  BulkCreateLookups,
  BulkCreateRowError,
  ProductsService,
} from '../products.service';
import { ConfirmDialogService } from '../../confirm-dialog/confirm-dialog.service';
import { AuthService } from '../../../auth/auth.service';
import { FormDraftStore } from '../../../shared/utils/form-draft-store';

type ColType = 'text' | 'number' | 'select' | 'bool';

/**
 * Where a select column's options come from. `categories`, `subCategories`,
 * `manufacturers` and `brands` depend on another cell of the same row and are
 * recomputed per row (see refreshRowOptions); the rest are grid-wide lists.
 */
type OptionSource =
  | 'departments'
  | 'categories'
  | 'subCategories'
  | 'vendors'
  | 'manufacturers'
  | 'brands'
  | 'gstSlabs'
  | 'uoms';

interface Option {
  id: string;
  name: string;
  /** ng-select groupBy label (manufacturers linked to the row's vendor). */
  group?: string;
}

export interface ColumnDef {
  key: string;
  label: string;
  type: ColType;
  /** Always shown; cannot be hidden from the Columns picker. */
  mandatory: boolean;
  /** Blank is an error on a non-empty row. */
  required?: boolean;
  width: number;
  options?: OptionSource;
  placeholder?: string;
  maxLength?: number;
  hint?: string;
}

interface GridRow {
  uid: number;
  values: Record<string, any>;
  errors: Record<string, string>;
  warnings: Record<string, string>;
  categoryOptions: Option[];
  subCategoryOptions: Option[];
  manufacturerOptions: Option[];
  brandOptions: Option[];
}

/** The confirm dialog renders its message as HTML. */
function escapeHtml(text: string): string {
  return String(text).replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
  );
}

/**
 * What the browser safety copy of the unsaved grid holds (see autosaveDraft):
 * each row's typed values, in grid order. Option lists are rebuilt on restore.
 */
interface BulkProductsDraftSnapshot {
  rows: Array<Record<string, any>>;
}

const COLUMN_PREFS_KEY = 'bulkAddProducts.visibleColumns';

/** Width of the row-number gutter; must match .row-num in the stylesheet. */
const ROW_NUM_WIDTH = 44;
/** Width of the trailing actions column; must match .row-actions. */
const ROW_ACTIONS_WIDTH = 76;

/** Columns pinned to the left of the grid, in on-screen order. */
const FROZEN_COLUMN_KEYS = ['sku', 'shortDescription'];
const INITIAL_ROWS = 10;
const MAX_ROWS = 500;
const BOOL_OPTIONS = [
  { id: true, name: 'Yes' },
  { id: false, name: 'No' },
];

/**
 * Bulk Add Products — a spreadsheet-like grid for keying in many SKUs at once.
 *
 * Every column maps 1:1 to a field of the single-product form and the same
 * masters feed the dropdowns (vendor → linked manufacturers → brands,
 * department → categories → sub-categories, GST slabs, UOM). The grid is
 * validated locally, then dry-run on the server (per-cell errors), and only
 * then saved — all rows or none, so a red cell never leaves half the sheet
 * created.
 *
 * The SKU column follows the client setting "SKU entry on Bulk Add Products":
 * MANUAL = typed here, AUTO = generated from the classification codes on save.
 */
@Component({
  selector: 'app-bulk-add-products',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, NgSelectComponent],
  templateUrl: './bulk-add-products.component.html',
  styleUrls: ['./bulk-add-products.component.scss'],
})
export class BulkAddProductsComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private readonly productService = inject(ProductsService);
  private readonly toastr = inject(ToastrService);
  private readonly modalService = inject(NgbModal);
  private readonly confirmDialog = inject(ConfirmDialogService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly zone = inject(NgZone);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);

  @ViewChild('columnsModal') columnsModal!: TemplateRef<unknown>;

  /** Column catalogue. Order here is the on-screen order. */
  readonly allColumns: ColumnDef[] = [
    {
      key: 'sku',
      label: 'SKU',
      type: 'text',
      mandatory: true,
      required: true,
      width: 130,
      maxLength: 30,
    },
    {
      key: 'shortDescription',
      label: 'Short Description',
      type: 'text',
      mandatory: true,
      required: true,
      width: 220,
      maxLength: 60,
    },
    {
      key: 'vendorsId',
      label: 'Vendor',
      type: 'select',
      mandatory: true,
      required: true,
      width: 200,
      options: 'vendors',
    },
    {
      key: 'manufacturerId',
      label: 'Manufacturer',
      type: 'select',
      mandatory: true,
      width: 190,
      options: 'manufacturers',
    },
    {
      key: 'brandId',
      label: 'Brand',
      type: 'select',
      mandatory: true,
      width: 170,
      options: 'brands',
    },
    {
      key: 'departmentId',
      label: 'Department',
      type: 'select',
      mandatory: true,
      required: true,
      width: 170,
      options: 'departments',
    },
    {
      key: 'categoryId',
      label: 'Category',
      type: 'select',
      mandatory: true,
      required: true,
      width: 170,
      options: 'categories',
    },
    {
      key: 'subCategoryId',
      label: 'Sub Category',
      type: 'select',
      mandatory: true,
      width: 170,
      options: 'subCategories',
    },
    {
      key: 'hsnCode',
      label: 'HSN Code',
      type: 'text',
      mandatory: true,
      required: true,
      width: 110,
      maxLength: 8,
      placeholder: '4/6/8 digits',
    },
    {
      key: 'purchaseGstRateId',
      label: 'GST Slab',
      type: 'select',
      mandatory: true,
      required: true,
      width: 120,
      options: 'gstSlabs',
    },
    {
      key: 'uom',
      label: 'UOM',
      type: 'select',
      mandatory: true,
      required: true,
      width: 100,
      options: 'uoms',
    },
    {
      key: 'basePrice',
      label: 'Base Price',
      type: 'number',
      mandatory: true,
      width: 110,
      hint: 'Auto-filled as Cost Price ÷ (1 + GST); editable',
    },
    {
      key: 'costPrice',
      label: 'Cost Price',
      type: 'number',
      mandatory: true,
      required: true,
      width: 110,
    },
    {
      key: 'sellingPrice',
      label: 'Selling Price',
      type: 'number',
      mandatory: true,
      required: true,
      width: 110,
    },
    {
      key: 'mrp',
      label: 'MRP',
      type: 'number',
      mandatory: true,
      required: true,
      width: 100,
    },
    {
      key: 'marginPercentage',
      label: 'Margin (%)',
      type: 'number',
      mandatory: false,
      width: 100,
    },
    {
      key: 'eanCode',
      label: 'EAN Code',
      type: 'text',
      mandatory: false,
      width: 140,
      maxLength: 20,
      placeholder: 'EAN or SKU',
    },
    {
      key: 'pluCode',
      label: 'PLU (weighable)',
      type: 'text',
      mandatory: false,
      width: 120,
      maxLength: 20,
    },
    {
      key: 'isWeighable',
      label: 'Is Weighable',
      type: 'bool',
      mandatory: false,
      width: 110,
    },
    {
      key: 'longDescription',
      label: 'Long Description',
      type: 'text',
      mandatory: false,
      width: 240,
      maxLength: 150,
      hint: 'Blank = same as Short Description',
    },
    {
      key: 'isPerishable',
      label: 'Is Perishable',
      type: 'bool',
      mandatory: false,
      width: 110,
    },
    {
      key: 'isTemperatureControlled',
      label: 'Temp. Controlled',
      type: 'bool',
      mandatory: false,
      width: 120,
    },
    {
      key: 'shelfLifeDays',
      label: 'Shelf Life (days)',
      type: 'number',
      mandatory: false,
      width: 120,
    },
    {
      key: 'minShelfLifeDays',
      label: 'Min Shelf Life (days)',
      type: 'number',
      mandatory: false,
      width: 140,
    },
    {
      key: 'minShelfLifePercent',
      label: 'Min Shelf Life (%)',
      type: 'number',
      mandatory: false,
      width: 130,
    },
    {
      key: 'requiresBatchExpiryTracking',
      label: 'Batch Controlled',
      type: 'bool',
      mandatory: false,
      width: 120,
    },
    {
      key: 'serialNumberControlled',
      label: 'Serial Controlled',
      type: 'bool',
      mandatory: false,
      width: 120,
    },
    {
      key: 'flowThrough',
      label: 'Flow Through',
      type: 'bool',
      mandatory: false,
      width: 110,
    },
    {
      key: 'returnToVendor',
      label: 'Return to Vendor',
      type: 'bool',
      mandatory: false,
      width: 120,
    },
    {
      key: 'isConsumable',
      label: 'Is Consumable',
      type: 'bool',
      mandatory: false,
      width: 110,
    },
    {
      key: 'isDSD',
      label: 'Is DSD',
      type: 'bool',
      mandatory: false,
      width: 90,
    },
    {
      key: 'isKit',
      label: 'Kit Component',
      type: 'bool',
      mandatory: false,
      width: 110,
    },
    {
      key: 'cessApplicable',
      label: 'Cess Applicable',
      type: 'bool',
      mandatory: false,
      width: 110,
    },
    {
      key: 'cessRate',
      label: 'Cess Rate (%)',
      type: 'number',
      mandatory: false,
      width: 110,
    },
    {
      key: 'innerPackaging',
      label: 'Inner Packaging',
      type: 'number',
      mandatory: false,
      width: 120,
    },
    {
      key: 'outerPackaging',
      label: 'Outer Packaging',
      type: 'number',
      mandatory: false,
      width: 120,
    },
    {
      key: 'productSpecificPaymentTerms',
      label: 'Payment Terms (days)',
      type: 'number',
      mandatory: false,
      width: 140,
    },
    {
      key: 'isBulk',
      label: 'Is Bulk',
      type: 'bool',
      mandatory: false,
      width: 90,
    },
    {
      key: 'bulkParentSku',
      label: 'Bulk Parent SKU',
      type: 'text',
      mandatory: false,
      width: 140,
      maxLength: 30,
    },
    {
      key: 'conversionFactor',
      label: 'Pack Size (g)',
      type: 'number',
      mandatory: false,
      width: 110,
    },
    {
      key: 'colour',
      label: 'Colour',
      type: 'text',
      mandatory: false,
      width: 110,
      maxLength: 20,
    },
    {
      key: 'size',
      label: 'Size',
      type: 'text',
      mandatory: false,
      width: 100,
      maxLength: 20,
    },
    {
      key: 'weight',
      label: 'Weight (kg)',
      type: 'number',
      mandatory: false,
      width: 100,
    },
    {
      key: 'photoUrl',
      label: 'Photo URL',
      type: 'text',
      mandatory: false,
      width: 200,
    },
  ];

  readonly mandatoryColumns = this.allColumns.filter((c) => c.mandatory);
  readonly optionalColumns = this.allColumns.filter((c) => !c.mandatory);
  readonly boolOptions = BOOL_OPTIONS;

  /** Optional columns currently shown (persisted per browser). */
  visibleOptionalKeys = new Set<string>([
    'eanCode',
    'marginPercentage',
    'isWeighable',
  ]);
  /** Draft selection while the Columns picker is open. */
  pickerSelection = new Set<string>();
  visibleColumns: ColumnDef[] = [];
  /** Frozen column key -> sticky left offset in px (see rebuildFrozenOffsets). */
  frozenLeft: Record<string, number | undefined> = {};
  /** Rightmost frozen column, which carries the drop shadow. */
  lastFrozenKey: string | null = null;
  /**
   * Explicit table width = gutter + every visible column + actions. With
   * table-layout: fixed this pins each column to its declared width, so the
   * frozen offsets computed from those widths line up exactly. A table left
   * at width:auto stretches to the container and shifts every column.
   */
  tableWidth = 0;

  lookups: BulkCreateLookups | null = null;
  loadingLookups = true;
  lookupsError = false;

  departmentOptions: Option[] = [];
  vendorOptions: Option[] = [];
  gstOptions: Option[] = [];
  uomOptions: Option[] = [];
  /** Every manufacturer, under one ng-select group so groupBy always has a label. */
  private allManufacturerOptions: Option[] = [];
  private categoriesByDept = new Map<string, Option[]>();
  private subCategoriesByCat = new Map<string, Option[]>();
  private brandsByMfg = new Map<string, Option[]>();
  private manufacturersByVendor = new Map<string, Set<string>>();
  private gstRateById = new Map<string, number>();

  rows: GridRow[] = [];
  private nextUid = 1;
  /** Last focused cell; fill-down copies from here. */
  activeCell: { row: number; col: string } | null = null;

  saving = false;
  validating = false;
  submitted = false;
  /** Flat error list shown under the grid, in row order. */
  errorList: BulkCreateRowError[] = [];
  warningList: BulkCreateRowError[] = [];
  lastResult: { created: number; skus: string[] } | null = null;

  get skuEntryMode(): 'MANUAL' | 'AUTO' {
    return this.lookups?.skuEntryMode ?? 'MANUAL';
  }

  get skuIsAuto(): boolean {
    return this.skuEntryMode === 'AUTO';
  }

  get filledRowCount(): number {
    return this.rows.filter((r) => !this.isRowEmpty(r)).length;
  }

  ngOnInit(): void {
    this.restoreColumnPrefs();
    this.rebuildVisibleColumns();
    this.rows = [];
    this.addRows(INITIAL_ROWS);
    this.initDraft(); // restore is offered once the dropdown data has loaded
    this.loadLookups();
  }

  ngAfterViewInit(): void {
    this.scheduleFrozenSync();
  }

  ngOnDestroy(): void {
    if (this.draftTimer) clearInterval(this.draftTimer);
    this.autosaveDraft();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.scheduleFrozenSync();
  }

  // ───────────────────────── lookups ─────────────────────────

  loadLookups(): void {
    this.loadingLookups = true;
    this.lookupsError = false;
    this.productService.getBulkCreateLookups().subscribe({
      next: (lk) => {
        this.lookups = lk;
        this.indexLookups(lk);
        this.rows.forEach((r) => this.refreshRowOptions(r));
        this.rebuildVisibleColumns();
        this.loadingLookups = false;
        this.cdr.markForCheck();
        // Restored rows need these lists for their dropdowns. A retry after a
        // failed load must not ask twice.
        if (!this.draftOffered) {
          this.draftOffered = true;
          setTimeout(() => this.offerDraftRestore());
        }
      },
      error: () => {
        this.loadingLookups = false;
        this.lookupsError = true;
        this.toastr.error('Could not load dropdown data. Reload to try again.');
      },
    });
  }

  private indexLookups(lk: BulkCreateLookups): void {
    this.departmentOptions = lk.departments.map((d) => ({
      id: d.id,
      name: d.name,
    }));
    this.vendorOptions = lk.vendors.map((v) => ({ id: v.id, name: v.name }));
    this.gstOptions = lk.gstSlabs.map((g) => ({
      id: g.id,
      name: g.displayLabel || `${g.value}%`,
    }));
    this.uomOptions = lk.uoms.map((u) => ({ id: u, name: u }));
    this.allManufacturerOptions = lk.manufacturers.map((m) => ({
      id: m.id,
      name: m.name,
      group: 'All manufacturers',
    }));

    this.gstRateById = new Map(
      lk.gstSlabs.map((g) => [g.id, Number(g.value) || 0]),
    );

    this.categoriesByDept = new Map();
    lk.categories.forEach((c) => {
      if (!c.departmentsId) return;
      if (!this.categoriesByDept.has(c.departmentsId))
        this.categoriesByDept.set(c.departmentsId, []);
      this.categoriesByDept
        .get(c.departmentsId)!
        .push({ id: c.id, name: c.name });
    });

    this.subCategoriesByCat = new Map();
    lk.subCategories.forEach((s) => {
      if (!this.subCategoriesByCat.has(s.categoryId))
        this.subCategoriesByCat.set(s.categoryId, []);
      this.subCategoriesByCat
        .get(s.categoryId)!
        .push({ id: s.id, name: s.name });
    });

    this.brandsByMfg = new Map();
    lk.brands.forEach((b) => {
      if (!this.brandsByMfg.has(b.manufacturerId))
        this.brandsByMfg.set(b.manufacturerId, []);
      this.brandsByMfg.get(b.manufacturerId)!.push({ id: b.id, name: b.name });
    });

    this.manufacturersByVendor = new Map();
    lk.vendorManufacturers.forEach((l) => {
      if (!this.manufacturersByVendor.has(l.vendorId))
        this.manufacturersByVendor.set(l.vendorId, new Set());
      this.manufacturersByVendor.get(l.vendorId)!.add(l.manufacturerId);
    });
  }

  /**
   * Recompute the dependent dropdowns of one row. Manufacturers linked to the
   * vendor are listed first under their own group, like the single form's
   * vendor-driven manufacturer list, but every manufacturer stays pickable —
   * the backend links the pair on save.
   */
  private refreshRowOptions(row: GridRow): void {
    const v = row.values;
    row.categoryOptions = v['departmentId']
      ? (this.categoriesByDept.get(v['departmentId']) ?? [])
      : [];
    row.subCategoryOptions = v['categoryId']
      ? (this.subCategoriesByCat.get(v['categoryId']) ?? [])
      : [];
    row.brandOptions = v['manufacturerId']
      ? (this.brandsByMfg.get(v['manufacturerId']) ?? [])
      : [];

    const linked = v['vendorsId']
      ? this.manufacturersByVendor.get(v['vendorsId'])
      : undefined;
    if (linked && linked.size) {
      const first: Option[] = [];
      const rest: Option[] = [];
      this.allManufacturerOptions.forEach((m) =>
        (linked.has(m.id) ? first : rest).push({
          ...m,
          group: linked.has(m.id) ? 'Linked to vendor' : 'Other manufacturers',
        }),
      );
      row.manufacturerOptions = [...first, ...rest];
    } else {
      row.manufacturerOptions = this.allManufacturerOptions;
    }
  }

  optionsFor(row: GridRow, col: ColumnDef): Option[] {
    switch (col.options) {
      case 'departments':
        return this.departmentOptions;
      case 'categories':
        return row.categoryOptions;
      case 'subCategories':
        return row.subCategoryOptions;
      case 'vendors':
        return this.vendorOptions;
      case 'manufacturers':
        return row.manufacturerOptions;
      case 'brands':
        return row.brandOptions;
      case 'gstSlabs':
        return this.gstOptions;
      case 'uoms':
        return this.uomOptions;
      default:
        return [];
    }
  }

  // ───────────────────────── columns ─────────────────────────

  private restoreColumnPrefs(): void {
    try {
      const raw = localStorage.getItem(COLUMN_PREFS_KEY);
      if (raw) {
        const keys: string[] = JSON.parse(raw);
        const valid = new Set(this.optionalColumns.map((c) => c.key));
        this.visibleOptionalKeys = new Set(keys.filter((k) => valid.has(k)));
      }
    } catch {
      /* corrupt prefs: fall back to defaults */
    }
  }

  private rebuildVisibleColumns(): void {
    this.visibleColumns = this.allColumns.filter(
      (c) => c.mandatory || this.visibleOptionalKeys.has(c.key),
    );
    this.tableWidth =
      ROW_NUM_WIDTH +
      this.visibleColumns.reduce((sum, c) => sum + c.width, 0) +
      ROW_ACTIONS_WIDTH;
    this.rebuildFrozenOffsets();
    this.scheduleFrozenSync();
  }

  /**
   * Left offset (px) for each frozen column, measured from the scroll edge:
   * the row-number gutter plus the widths of the frozen columns before it.
   * Keys absent from the map are not frozen. Only a LEADING run of frozen
   * columns is pinned: a frozen column sitting behind a scrolling one would
   * slide over it, which is the overlap a spreadsheet never shows.
   */
  private rebuildFrozenOffsets(
    widthOf: (col: ColumnDef) => number = (c) => c.width,
    gutter = ROW_NUM_WIDTH,
  ): void {
    const offsets: Record<string, number> = {};
    let left = gutter;
    for (const col of this.visibleColumns) {
      if (!FROZEN_COLUMN_KEYS.includes(col.key)) break;
      offsets[col.key] = left;
      left += widthOf(col);
    }
    this.frozenLeft = offsets;
    this.lastFrozenKey = Object.keys(offsets).pop() ?? null;
  }

  /**
   * Re-measure the rendered header cells and correct the frozen offsets.
   * Declared widths are the plan; the browser layout (borders, zoom, font
   * fallbacks) is the truth, and a 1px drift per column is enough to open a
   * seam beside a pinned cell.
   */
  private scheduleFrozenSync(): void {
    setTimeout(() => {
      const ths = Array.from(
        this.host.nativeElement.querySelectorAll('thead th'),
      ) as HTMLElement[];
      if (ths.length < this.visibleColumns.length + 1) return;
      const width = new Map<string, number>();
      this.visibleColumns.forEach((col, i) =>
        width.set(col.key, ths[i + 1].getBoundingClientRect().width),
      );
      const gutter = ths[0].getBoundingClientRect().width;
      this.rebuildFrozenOffsets((c) => width.get(c.key) ?? c.width, gutter);
      this.cdr.markForCheck();
    });
  }

  openColumnsPicker(): void {
    this.pickerSelection = new Set(this.visibleOptionalKeys);
    this.modalService.open(this.columnsModal, {
      centered: true,
      scrollable: true,
    });
  }

  togglePickerColumn(key: string, checked: boolean): void {
    if (checked) this.pickerSelection.add(key);
    else this.pickerSelection.delete(key);
  }

  get allOptionalSelected(): boolean {
    return this.optionalColumns.every((c) => this.pickerSelection.has(c.key));
  }

  toggleAllOptional(checked: boolean): void {
    this.pickerSelection = checked
      ? new Set(this.optionalColumns.map((c) => c.key))
      : new Set();
  }

  resetPicker(): void {
    this.pickerSelection = new Set([
      'eanCode',
      'marginPercentage',
      'isWeighable',
    ]);
  }

  applyColumns(): void {
    this.visibleOptionalKeys = new Set(this.pickerSelection);
    try {
      localStorage.setItem(
        COLUMN_PREFS_KEY,
        JSON.stringify(Array.from(this.visibleOptionalKeys)),
      );
    } catch {
      /* private mode etc. — selection still applies for this visit */
    }
    this.rebuildVisibleColumns();
  }

  isRequired(col: ColumnDef): boolean {
    if (col.key === 'sku') return !this.skuIsAuto;
    if (col.key === 'subCategoryId') return this.skuIsAuto;
    // Vendor / manufacturer / brand follow Configuration > Products. Until
    // the lookups arrive the column defaults apply (vendor required).
    const req = this.lookups?.requirements;
    if (col.key === 'vendorsId') return req ? req.vendorRequired : !!col.required;
    if (col.key === 'manufacturerId')
      return req ? req.manufacturerRequired : !!col.required;
    if (col.key === 'brandId') return req ? req.brandRequired : !!col.required;
    return !!col.required;
  }

  // ───────────────────────── rows ─────────────────────────

  private newRow(): GridRow {
    return {
      uid: this.nextUid++,
      values: {
        uom: 'PCS',
        returnToVendor: true,
        isWeighable: false,
        isPerishable: false,
        isTemperatureControlled: false,
        requiresBatchExpiryTracking: false,
        serialNumberControlled: false,
        flowThrough: false,
        isConsumable: false,
        isDSD: false,
        isKit: false,
        isBulk: false,
        cessApplicable: false,
      },
      errors: {},
      warnings: {},
      categoryOptions: [],
      subCategoryOptions: [],
      manufacturerOptions: this.allManufacturerOptions,
      brandOptions: [],
    };
  }

  addRows(count: number): void {
    const room = MAX_ROWS - this.rows.length;
    if (room <= 0) {
      this.toastr.warning(`The grid holds at most ${MAX_ROWS} rows per save.`);
      return;
    }
    for (let i = 0; i < Math.min(count, room); i++)
      this.rows.push(this.newRow());
  }

  removeRow(index: number): void {
    this.rows.splice(index, 1);
    if (!this.rows.length) this.addRows(1);
    this.rebuildErrorList();
  }

  /** Copy a row below itself, minus the identifiers that must be unique. */
  duplicateRow(index: number): void {
    if (this.rows.length >= MAX_ROWS) {
      this.toastr.warning(`The grid holds at most ${MAX_ROWS} rows per save.`);
      return;
    }
    const src = this.rows[index];
    const copy = this.newRow();
    copy.values = { ...src.values, sku: '', eanCode: '', pluCode: '' };
    this.refreshRowOptions(copy);
    this.rows.splice(index + 1, 0, copy);
  }

  async clearGrid(): Promise<void> {
    if (this.filledRowCount > 0) {
      const ok = await this.confirmDialog.confirm({
        title: 'Clear grid?',
        message: `Discard the ${this.filledRowCount} row(s) you have filled in?`,
        confirmText: 'Clear',
        cancelText: 'Keep',
      });
      if (!ok) return;
      // Discarded on purpose: the browser copy goes too. Not closed, so the
      // next rows typed here are still protected.
      this.draftStore?.clear();
    }
    this.rows = [];
    this.addRows(INITIAL_ROWS);
    this.errorList = [];
    this.warningList = [];
    this.lastResult = null;
  }

  /**
   * A row counts as empty when nothing beyond the defaults has been typed —
   * so the 10 starter rows never block a save of the 3 that were filled.
   */
  isRowEmpty(row: GridRow): boolean {
    return this.isBlankValues(row.values);
  }

  private isBlankValues(values: Record<string, any> | null | undefined): boolean {
    return !this.allColumns.some((c) => {
      if (c.type === 'bool' || c.key === 'uom') return false;
      const v = values?.[c.key];
      return v !== undefined && v !== null && String(v).trim() !== '';
    });
  }

  // ───────────────────────── cell editing ─────────────────────────

  setActive(rowIndex: number, colKey: string): void {
    this.activeCell = { row: rowIndex, col: colKey };
  }

  onCellChange(row: GridRow, colKey: string): void {
    delete row.errors[colKey];
    this.submitted = false;
    switch (colKey) {
      case 'departmentId':
        row.values['categoryId'] = null;
        row.values['subCategoryId'] = null;
        this.refreshRowOptions(row);
        break;
      case 'categoryId':
        row.values['subCategoryId'] = null;
        this.refreshRowOptions(row);
        break;
      case 'vendorsId':
        this.refreshRowOptions(row);
        break;
      case 'manufacturerId': {
        const brands = row.values['manufacturerId']
          ? (this.brandsByMfg.get(row.values['manufacturerId']) ?? [])
          : [];
        if (!brands.some((b) => b.id === row.values['brandId']))
          row.values['brandId'] = null;
        this.refreshRowOptions(row);
        break;
      }
      case 'costPrice':
        this.recalcPricing(row, 'cost');
        break;
      case 'basePrice':
        this.recalcPricing(row, 'base');
        break;
      case 'mrp':
        this.recalcPricing(row, 'mrp');
        break;
      case 'purchaseGstRateId':
        // A new GST rate changes the cost ↔ base relationship.
        if (this.num(row.values['costPrice']) !== null)
          this.recalcPricing(row, 'cost');
        break;
      case 'sku':
        if (typeof row.values['sku'] === 'string')
          row.values['sku'] = row.values['sku'].toUpperCase();
        break;
    }
    if (this.errorList.length) this.rebuildErrorList();
  }

  /**
   * Same derivations as the single form's calculatePricing(): base is always
   * cost net of GST; MRP follows the margin only while it is still blank
   * (or the cost has overtaken it), so a typed MRP is never overwritten.
   */
  private recalcPricing(row: GridRow, changed: 'cost' | 'base' | 'mrp'): void {
    const v = row.values;
    const gst = (this.gstRateById.get(v['purchaseGstRateId']) ?? 0) / 100;
    const margin = this.num(v['marginPercentage']) ?? 0;
    const r4 = (n: number) => Math.round(n * 10000) / 10000;

    if (changed === 'cost') {
      const cost = this.num(v['costPrice']);
      if (cost === null) return;
      v['basePrice'] = r4(cost / (1 + gst));
      const mrp = this.num(v['mrp']);
      if (margin > 0 && (mrp === null || cost > mrp))
        v['mrp'] = r4(margin >= 100 ? cost : cost / (1 - margin / 100));
    } else if (changed === 'base') {
      const base = this.num(v['basePrice']);
      if (base === null) return;
      const cost = r4(base * (1 + gst));
      v['costPrice'] = cost;
      const mrp = this.num(v['mrp']);
      if (margin > 0 && (mrp === null || cost > mrp))
        v['mrp'] = r4(margin >= 100 ? cost : cost / (1 - margin / 100));
    } else if (changed === 'mrp') {
      const mrp = this.num(v['mrp']);
      if (mrp === null || margin <= 0) return;
      const cost = r4(mrp * (1 - margin / 100));
      v['costPrice'] = cost;
      v['basePrice'] = r4(cost / (1 + gst));
    }
  }

  /** Copy the active cell (or row 1) of this column into every row below it. */
  fillDown(col: ColumnDef): void {
    const from = this.activeCell?.col === col.key ? this.activeCell.row : 0;
    const source = this.rows[from];
    if (!source) return;
    const value = source.values[col.key];
    for (let i = from + 1; i < this.rows.length; i++) {
      const row = this.rows[i];
      if (
        col.options === 'categories' ||
        col.options === 'subCategories' ||
        col.options === 'brands'
      ) {
        // Dependent value: only meaningful when the parent matches.
        if (!this.optionsFor(row, col).some((o) => o.id === value)) continue;
      }
      row.values[col.key] = value;
      this.onCellChange(row, col.key);
    }
    this.toastr.info(`${col.label} copied down from row ${from + 1}.`);
  }

  /** Enter moves to the same column on the next row, adding one at the end. */
  moveDown(event: Event, rowIndex: number, colKey: string): void {
    event.preventDefault();
    if (rowIndex === this.rows.length - 1) this.addRows(1);
    setTimeout(() => {
      this.focusCell(rowIndex + 2, colKey, false);
    });
  }

  /**
   * Spreadsheet paste: a block of tab/newline separated text fills across the
   * visible columns and down the rows starting at the pasted cell. Dropdown
   * cells match by option name, Yes/No cells by yes/true/1.
   */
  onPaste(event: ClipboardEvent, rowIndex: number, colKey: string): void {
    const text = event.clipboardData?.getData('text/plain') ?? '';
    if (!text.includes('\n') && !text.includes('\t')) return; // plain single-cell paste
    event.preventDefault();

    const lines = text.replace(/\r/g, '').split('\n');
    if (lines.length && lines[lines.length - 1] === '') lines.pop();
    const startCol = this.visibleColumns.findIndex((c) => c.key === colKey);
    if (startCol < 0) return;

    let pastedCells = 0;
    lines.forEach((line, li) => {
      const targetIndex = rowIndex + li;
      if (targetIndex >= MAX_ROWS) return;
      while (targetIndex >= this.rows.length) this.rows.push(this.newRow());
      const row = this.rows[targetIndex];
      line.split('\t').forEach((cell, ci) => {
        const col = this.visibleColumns[startCol + ci];
        if (!col) return;
        if (col.key === 'sku' && this.skuIsAuto) return;
        row.values[col.key] = this.coerceCell(row, col, cell.trim());
        this.onCellChange(row, col.key);
        pastedCells++;
      });
    });
    this.toastr.info(
      `Pasted ${pastedCells} cell(s) across ${lines.length} row(s).`,
    );
  }

  private coerceCell(row: GridRow, col: ColumnDef, raw: string): any {
    if (raw === '') return col.type === 'bool' ? row.values[col.key] : null;
    switch (col.type) {
      case 'number': {
        const n = Number(raw.replace(/[,₹\s]/g, ''));
        return Number.isFinite(n) ? n : null;
      }
      case 'bool':
        return /^(y|yes|true|1)$/i.test(raw);
      case 'select': {
        const needle = raw.toLowerCase();
        const options = this.optionsFor(row, col);
        const match =
          options.find((o) => o.name.toLowerCase() === needle) ??
          options.find((o) => o.id === raw);
        return match ? match.id : null;
      }
      default:
        return raw;
    }
  }

  private num(v: unknown): number | null {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }

  // ───────────────────────── validation & save ─────────────────────────

  private setError(row: GridRow, col: string, message: string): void {
    if (!row.errors[col]) row.errors[col] = message;
  }

  /** Cheap checks before the round trip; mirrors the single form's validators. */
  private validateLocally(rows: GridRow[]): boolean {
    let ok = true;
    const skus = new Map<string, number>();
    const eans = new Map<string, number>();
    rows.forEach((row) => {
      row.errors = {};
      const v = row.values;
      this.allColumns.forEach((col) => {
        if (!this.isRequired(col)) return;
        const val = v[col.key];
        if (val === undefined || val === null || String(val).trim() === '')
          this.setError(row, col.key, `${col.label} is required`);
      });

      const hsn = String(v['hsnCode'] ?? '').trim();
      if (hsn) {
        if (!/^\d+$/.test(hsn))
          this.setError(row, 'hsnCode', 'HSN Code must contain digits only');
        else if (![4, 6, 8].includes(hsn.length))
          this.setError(row, 'hsnCode', 'HSN Code must be 4, 6 or 8 digits');
      }

      if (!this.skuIsAuto) {
        const sku = String(v['sku'] ?? '')
          .trim()
          .toUpperCase();
        if (sku) {
          if (skus.has(sku))
            this.setError(
              row,
              'sku',
              `Duplicate SKU in this grid (also row ${skus.get(sku)})`,
            );
          else skus.set(sku, this.rows.indexOf(row) + 1);
        }
      }
      const ean = String(v['eanCode'] ?? '').trim();
      if (ean) {
        if (eans.has(ean))
          this.setError(
            row,
            'eanCode',
            `Duplicate EAN in this grid (also row ${eans.get(ean)})`,
          );
        else eans.set(ean, this.rows.indexOf(row) + 1);
      }

      [
        'mrp',
        'costPrice',
        'sellingPrice',
        'basePrice',
        'cessRate',
        'marginPercentage',
      ].forEach((k) => {
        const n = this.num(v[k]);
        if (n !== null && n < 0) this.setError(row, k, 'Cannot be negative');
      });
      if (v['cessApplicable'] && this.num(v['cessRate']) === null)
        this.setError(
          row,
          'cessRate',
          'Cess rate is required when cess is applicable',
        );

      const r3 = (n: number) => Math.round(n * 1000) / 1000;
      const mrp = this.num(v['mrp']);
      const cost = this.num(v['costPrice']);
      const sp = this.num(v['sellingPrice']);
      const base = this.num(v['basePrice']);
      if (mrp !== null && cost !== null && sp !== null) {
        if (base !== null && r3(base) > r3(cost))
          this.setError(
            row,
            'basePrice',
            'Base Price must be less than or equal to Cost Price',
          );
        if (r3(mrp) < r3(cost))
          this.setError(
            row,
            'mrp',
            'MRP should be greater than or equal to Cost Price',
          );
        if (r3(sp) < r3(cost))
          this.setError(
            row,
            'sellingPrice',
            'Selling Price should be greater than or equal to Cost Price',
          );
        if (r3(sp) > r3(mrp))
          this.setError(
            row,
            'sellingPrice',
            'Selling Price can be same as MRP but not greater',
          );
      }

      if (Object.keys(row.errors).length) ok = false;
    });
    return ok;
  }

  private toPayload(row: GridRow, rowNumber: number): Record<string, unknown> {
    const out: Record<string, unknown> = { rowNumber };
    this.allColumns.forEach((col) => {
      const v = row.values[col.key];
      if (col.key === 'sku' && this.skuIsAuto) return;
      switch (col.type) {
        case 'bool':
          out[col.key] = !!v;
          break;
        case 'number': {
          const n = this.num(v);
          if (n !== null) out[col.key] = n;
          break;
        }
        default: {
          const s = v === null || v === undefined ? '' : String(v).trim();
          if (s) out[col.key] = s;
        }
      }
    });
    return out;
  }

  private applyServerErrors(
    errors: BulkCreateRowError[],
    warnings: BulkCreateRowError[],
  ): void {
    errors.forEach((e) => {
      const row = this.rows[e.row - 1];
      if (row) this.setError(row, e.column, e.error);
    });
    warnings.forEach((w) => {
      const row = this.rows[w.row - 1];
      if (row) row.warnings[w.column] = w.error;
    });
    this.rebuildErrorList();
  }

  private rebuildErrorList(): void {
    const list: BulkCreateRowError[] = [];
    const warns: BulkCreateRowError[] = [];
    this.rows.forEach((row, i) => {
      Object.entries(row.errors).forEach(([column, error]) =>
        list.push({ row: i + 1, column, error }),
      );
      Object.entries(row.warnings).forEach(([column, error]) =>
        warns.push({ row: i + 1, column, error }),
      );
    });
    this.errorList = list;
    this.warningList = warns;
  }

  columnLabel(key: string): string {
    return this.allColumns.find((c) => c.key === key)?.label ?? key;
  }

  /** Focus the editor of a cell by 1-based grid row; ng-select cells focus their search box. */
  focusCell(rowNumber: number, colKey: string, scroll = true): void {
    const sel = `[data-cell="${rowNumber - 1}:${colKey}"]`;
    const el = this.host.nativeElement.querySelector(
      `${sel} input, ${sel} select`,
    ) as HTMLElement | null;
    if (!el) return;
    el.focus();
    if (scroll) el.scrollIntoView({ block: 'center', inline: 'center' });
  }

  async save(): Promise<void> {
    if (this.saving || this.validating) return;
    const filled = this.rows.filter((r) => !this.isRowEmpty(r));
    this.rows.forEach((r) => {
      r.errors = {};
      r.warnings = {};
    });
    this.lastResult = null;
    if (!filled.length) {
      this.toastr.warning('Fill in at least one row before saving.');
      return;
    }

    if (!this.validateLocally(filled)) {
      this.rebuildErrorList();
      this.toastr.error(
        `${new Set(this.errorList.map((e) => e.row)).size} row(s) need fixing.`,
      );
      return;
    }

    const payload = filled.map((row) =>
      this.toPayload(row, this.rows.indexOf(row) + 1),
    );

    this.validating = true;
    this.productService.validateBulkCreate(payload).subscribe({
      next: async (res) => {
        this.validating = false;
        this.applyServerErrors(res.errors, res.warnings);
        if (res.errors.length) {
          this.toastr.error(
            `${res.errorRows} of ${res.totalRows} row(s) need fixing. Nothing was saved.`,
          );
          return;
        }
        const warnNote = res.warnings.length
          ? `<br><small class="text-warning-emphasis">${res.warnings.length} HSN warning(s) — the codes are not in the HSN master. They will still be saved.</small>`
          : '';
        const ok = await this.confirmDialog.confirm({
          title: 'Create products?',
          message: `Create <strong>${filled.length}</strong> product(s)${
            this.skuIsAuto ? ' with auto-generated SKUs' : ''
          }? This saves all rows together — none are created if any fails.${warnNote}`,
          confirmText: `Create ${filled.length}`,
          cancelText: 'Back to grid',
        });
        if (!ok) return;
        this.commit(payload);
      },
      error: (err) => {
        this.validating = false;
        this.handleHttpError(err, 'Validation failed');
      },
    });
  }

  private commit(payload: Record<string, unknown>[]): void {
    this.saving = true;
    this.productService.bulkCreateProducts(payload).subscribe({
      next: (res) => {
        this.saving = false;
        this.submitted = true;
        this.lastResult = {
          created: res.created,
          skus: res.products.map((p) => p.sku),
        };
        this.toastr.success(res.message || `Created ${res.created} product(s)`);
        // All-or-nothing save succeeded: drop the copy. Not closed, since the
        // grid resets for the next batch, which needs the same protection.
        this.draftStore?.clear();
        this.rows = [];
        this.addRows(INITIAL_ROWS);
        this.errorList = [];
        this.warningList = [];
        window.scrollTo({ top: 0, behavior: 'smooth' });
      },
      error: (err) => {
        this.saving = false;
        this.handleHttpError(err, 'Save failed');
      },
    });
  }

  private handleHttpError(err: any, fallback: string): void {
    const body = err?.error ?? {};
    const errors: BulkCreateRowError[] = Array.isArray(body.errors)
      ? body.errors
      : [];
    if (errors.length)
      this.applyServerErrors(
        errors,
        Array.isArray(body.warnings) ? body.warnings : [],
      );
    const message =
      typeof body.message === 'string'
        ? body.message
        : Array.isArray(body.message)
          ? body.message.join('; ')
          : fallback;
    this.toastr.error(message, undefined, { timeOut: 8000 });
  }

  goToList(): void {
    this.router.navigate(['/product-list']);
  }

  async canDeactivate(): Promise<boolean> {
    if (this.submitted || this.filledRowCount === 0) return true;
    const leave = await this.confirmDialog.confirm({
      title: 'Leave page?',
      message: `You have <strong>${this.filledRowCount}</strong> unsaved row(s) on the Bulk Add grid. Leave and discard them?`,
      confirmText: 'Leave',
      cancelText: 'Stay',
      size: 'lg',
    });
    // Leaving on purpose discards the browser copy too. Not when this is
    // the sign-out path (no token by now): that copy is the user's way back.
    if (leave && this.authService.isLoggedIn()) this.discardDraft();
    return leave;
  }

  // ── Unsaved-work safety copy ────────────────────────────────────────────
  // Prod 2026-10-29: a 140-line GRN was lost when the session ran out before
  // saving. This grid is just as long to key in, so it copies itself to the
  // browser every few seconds (FormDraftStore) and offers the copy back.
  private draftStore: FormDraftStore<BulkProductsDraftSnapshot> | null = null;
  private draftTimer: ReturnType<typeof setInterval> | null = null;
  /** False until the restore question is settled, so the still-blank grid
   *  cannot overwrite the copy it is about to offer. */
  private draftReady = false;
  /** Discarded on purpose (Leave): stop copying. */
  private draftClosed = false;
  /** The restore question is asked once per visit (lookups can be retried). */
  private draftOffered = false;

  private initDraft(): void {
    this.draftStore = new FormDraftStore<BulkProductsDraftSnapshot>([
      'bulk-products',
      this.authService.getUserId(),
      this.authService.getActiveLocationId(),
      'new',
    ]);
    // Outside Angular: the timer must not trigger change detection.
    this.zone.runOutsideAngular(() => {
      this.draftTimer = setInterval(() => this.autosaveDraft(), 5000);
    });
  }

  private draftRowCount(d: Partial<BulkProductsDraftSnapshot> | null | undefined): number {
    return (d?.rows ?? []).filter((v) => !this.isBlankValues(v)).length;
  }

  private draftSnapshot(): BulkProductsDraftSnapshot {
    return { rows: this.rows.map((r) => r.values) };
  }

  /** Also on pagehide: a closed tab or browser keeps the last few seconds. */
  @HostListener('window:pagehide')
  autosaveDraft(): void {
    if (!this.draftStore || !this.draftReady || this.draftClosed) return;
    const snapshot = this.draftSnapshot();
    // An emptied grid leaves any earlier copy alone ("Not now" kept it).
    if (this.draftRowCount(snapshot) > 0) this.draftStore.save(snapshot);
  }

  private discardDraft(): void {
    this.draftClosed = true;
    this.draftStore?.clear();
  }

  /** Offer a copy left by an earlier visit. */
  private async offerDraftRestore(): Promise<void> {
    const stored = this.draftStore?.load();
    const count = this.draftRowCount(stored?.data);
    if (!stored || !count) {
      this.draftReady = true;
      return;
    }

    const when = new Date(stored.savedAt).toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
    const firstName = stored.data.rows
      .map((v) => String(v?.['shortDescription'] ?? '').trim())
      .find(Boolean);
    const details = [
      `<strong>${count}</strong> row${count === 1 ? '' : 's'}`,
      firstName ? `starting with <strong>${escapeHtml(firstName)}</strong>` : '',
    ].filter(Boolean);
    const choice = await this.confirmDialog.choose({
      title: 'Restore unsaved products?',
      message:
        `These products were not saved. A copy from ${when} is still on this computer: ${details.join(', ')}.` +
        '<br><small class="text-muted">"Not now" keeps the copy, but it is replaced once you start entering products here.</small>',
      confirmText: 'Restore',
      altText: 'Discard',
      cancelText: 'Not now',
      size: 'lg',
    });
    if (choice === 'confirm') this.applyDraft(stored.data);
    else if (choice === 'alt') this.draftStore!.clear();
    this.draftReady = true;
  }

  private applyDraft(d: BulkProductsDraftSnapshot): void {
    this.rows = [];
    (d.rows ?? []).slice(0, MAX_ROWS).forEach((values) => {
      const row = this.newRow();
      row.values = { ...row.values, ...values };
      this.refreshRowOptions(row);
      this.rows.push(row);
    });
    if (!this.rows.length) this.addRows(INITIAL_ROWS);
    this.errorList = [];
    this.warningList = [];
    this.lastResult = null;
    this.submitted = false;
    this.cdr.detectChanges();
    this.scheduleFrozenSync();
    this.toastr.success(
      `${this.filledRowCount} row(s) restored. Save to create the products.`,
      'Products restored',
    );
  }

  trackRow(_: number, row: GridRow): number {
    return row.uid;
  }

  trackCol(_: number, col: ColumnDef): string {
    return col.key;
  }
}
